// src/pages/Dashboard.tsx
// Plezyr Admin - Dating App Moderation Dashboard
import React, { useEffect, useState, memo } from 'react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Avatar,
  Alert,
  Chip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReportIcon from '@mui/icons-material/Report';
import BlockIcon from '@mui/icons-material/Block';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAdminOverview } from '../services/adminService';
import { AdminOverviewResponse } from '../types/admin';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminOverviewResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAdminOverview();
        setData(response);
      } catch (err: any) {
        console.error('Failed to fetch dashboard data:', err);
        setError(err.response?.data?.detail || 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: t('dashboard.totalUsers', 'Utilisateurs totaux'),
      value: data?.users?.total || 0,
      icon: <PeopleIcon sx={{ fontSize: 28, color: '#9333EA' }} />,
      color: '#9333EA',
      bgColor: '#F3E8FF',
      onClick: () => navigate('/superadmin'),
    },
    {
      title: t('dashboard.activeToday', 'Actifs (7j)'),
      value: data?.engagement?.active_users_7d || 0,
      icon: <TrendingUpIcon sx={{ fontSize: 28, color: '#10b981' }} />,
      color: '#10b981',
      bgColor: '#D1FAE5',
    },
    {
      title: t('dashboard.pendingVerifications', 'Vérifications'),
      value: data?.summary?.pending_verifications || 0,
      icon: <VerifiedUserIcon sx={{ fontSize: 28, color: '#f59e0b' }} />,
      color: '#f59e0b',
      bgColor: '#FEF3C7',
      onClick: () => navigate('/verification'),
    },
    {
      title: t('sidebar.reports', 'Signalements'),
      value: data?.summary?.pending_reports || 0,
      icon: <ReportIcon sx={{ fontSize: 28, color: '#ef4444' }} />,
      color: '#ef4444',
      bgColor: '#FEE2E2',
      onClick: () => navigate('/reports'),
    },
    {
      title: t('sidebar.blocked', 'Bloqués'),
      value: data?.summary?.blocked_users || 0,
      icon: <BlockIcon sx={{ fontSize: 28, color: '#64748b' }} />,
      color: '#64748b',
      bgColor: '#F1F5F9',
      onClick: () => navigate('/blocked'),
    },
  ];

  // Dating specific stats
  const datingStats = [
    {
      title: 'Swipes total',
      value: data?.content?.posts?.total || 0,
      today: data?.content?.posts?.today || 0,
      icon: <FavoriteIcon sx={{ fontSize: 24, color: '#ec4899' }} />,
      color: '#ec4899',
    },
    {
      title: 'Matches',
      value: data?.content?.comments?.total || 0,
      today: data?.content?.comments?.today || 0,
      icon: <StarIcon sx={{ fontSize: 24, color: '#8b5cf6' }} />,
      color: '#8b5cf6',
    },
    {
      title: 'Vues profil',
      value: data?.content?.reactions?.total || 0,
      today: data?.content?.reactions?.today || 0,
      icon: <VisibilityIcon sx={{ fontSize: 24, color: '#06b6d4' }} />,
      color: '#06b6d4',
    },
    {
      title: 'Superlikes',
      value: data?.content?.reposts?.total || 0,
      icon: <StarIcon sx={{ fontSize: 24, color: '#f59e0b' }} />,
      color: '#f59e0b',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Welcome Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 2,
          p: 3,
          mb: 3,
          border: '1px solid #e2e8f0'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#9333EA',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'white'
            }}
          >
            {authState.user?.name?.charAt(0).toUpperCase() || 'A'}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={600} color="#1e293b">
              {t('dashboard.title')}, {authState.user?.name || 'Admin'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Plateforme d'administration Plezyr • {data?.users?.growth_rate_7d || '0%'} cette semaine
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Alerts */}
      {data?.alerts && data.alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {data.alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.type === 'error' ? 'error' : alert.type === 'warning' ? 'warning' : 'info'}
              sx={{ mb: 1, cursor: 'pointer' }}
              onClick={() => navigate(alert.action_url)}
            >
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Main Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card
              onClick={stat.onClick}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: stat.onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: stat.color,
                  boxShadow: `0 4px 12px ${stat.color}20`,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: stat.bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="#1e293b">
                      {stat.value.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {stat.title}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dating Stats */}
      <Typography variant="h6" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
        Activité Dating
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {datingStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="#1e293b">
                      {stat.value.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.title}
                    </Typography>
                    {stat.today !== undefined && (
                      <Chip
                        label={`+${stat.today} aujourd'hui`}
                        size="small"
                        sx={{
                          mt: 1,
                          bgcolor: `${stat.color}15`,
                          color: stat.color,
                          fontWeight: 500,
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                  {stat.icon}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                Utilisateurs
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Vérifiés</Typography>
                  <Typography fontWeight={600}>{data?.users?.verified || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Non vérifiés</Typography>
                  <Typography fontWeight={600}>{data?.users?.unverified || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Bannis</Typography>
                  <Typography fontWeight={600} color="error.main">{data?.users?.banned || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Nouveaux (30j)</Typography>
                  <Typography fontWeight={600} color="success.main">{data?.users?.new_30d || 0}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                Signalements
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Total</Typography>
                  <Typography fontWeight={600}>{data?.feedbacks?.total || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">En attente</Typography>
                  <Typography fontWeight={600} color="warning.main">{data?.feedbacks?.pending || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Résolus</Typography>
                  <Typography fontWeight={600} color="success.main">{data?.feedbacks?.resolved || 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Taux de match</Typography>
                  <Typography fontWeight={600}>{data?.engagement?.engagement_rate || '0%'}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default memo(Dashboard);
